import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  Plus, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  ArrowRightLeft,
  ArrowRight,
  Hash,
  Building2,
  FileText,
  Filter
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { RollInRequestForm } from './components/change-management/RollInRequestForm';
import { RolloutRequestForm } from './components/change-management/RolloutRequestForm';
import { AccountNumberChangeForm } from './components/change-management/AccountNumberChangeForm';
import { SiteDisconnectionForm } from './components/change-management/SiteDisconnectionForm';
import { ChangeRequestList } from './components/change-management/ChangeRequestList';
import { ChangeRequestApprovalPanel } from './components/change-management/ChangeRequestApprovalPanel';
import { useModuleAccess } from '@/hooks/useModuleAccess';

export default function ChangeManagement() {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const workspaceId = currentWorkspace?.id;
  
  const [activeTab, setActiveTab] = useState('requests');
  const [selectedRequestType, setSelectedRequestType] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'create'>('list');
  
  const moduleAccess = useModuleAccess('workspace', workspaceId || '');
  const isAdmin = moduleAccess.isOwner || moduleAccess.hasPermission('workspace_admin', 'admin');

  // Fetch pending requests count
  const { data: pendingCounts } = useQuery({
    queryKey: ['change-management-counts', workspaceId, projectId],
    queryFn: async () => {
      if (!workspaceId) return { roll_in: 0, rollout: 0, account_change: 0, site_disconnect: 0, total: 0 };
      
      const { data, error } = await supabase
        .from('tum_change_management_requests')
        .select('request_type, status')
        .eq('workspace_id', workspaceId)
        .in('status', ['pending', 'under_review']);
      
      if (error) throw error;
      
      const counts = {
        roll_in: 0,
        rollout: 0,
        account_change: 0,
        site_disconnect: 0,
        total: 0,
      };
      
      data?.forEach((req) => {
        if (req.status === 'pending' || req.status === 'under_review') {
          counts.total++;
          if (req.request_type === 'roll_in') counts.roll_in++;
          else if (req.request_type === 'rollout') counts.rollout++;
          else if (req.request_type === 'account_number_change') counts.account_change++;
          else if (req.request_type === 'site_disconnection') counts.site_disconnect++;
        }
      });
      
      return counts;
    },
    enabled: !!workspaceId,
  });

  const handleCreateRequest = (type: string) => {
    setSelectedRequestType(type);
    setViewMode('create');
    setActiveTab(type);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedRequestType(null);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/w/${workspaceId}/dashboard/tum/onboarding${projectId ? `/project/${projectId}` : ''}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Onboarding
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Change Management</h1>
            <p className="text-muted-foreground">
              Manage account roll-ins, rollouts, number changes, and site disconnections
            </p>
          </div>
        </div>
        {isAdmin && pendingCounts && pendingCounts.total > 0 && (
          <Badge variant="destructive" className="gap-2">
            <Clock className="h-3 w-3" />
            {pendingCounts.total} Pending
          </Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="requests">
            <FileText className="h-4 w-4 mr-2" />
            All Requests
            {pendingCounts && pendingCounts.total > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {pendingCounts.total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="roll_in">
            <ArrowRight className="h-4 w-4 mr-2" />
            Roll Ins
            {pendingCounts && pendingCounts.roll_in > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs">{pendingCounts.roll_in}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="rollout">
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Rollouts
            {pendingCounts && pendingCounts.rollout > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs">{pendingCounts.rollout}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="account_number_change">
            <Hash className="h-4 w-4 mr-2" />
            Account Change
            {pendingCounts && pendingCounts.account_change > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs">{pendingCounts.account_change}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="site_disconnection">
            <Building2 className="h-4 w-4 mr-2" />
            Site Disconnect
            {pendingCounts && pendingCounts.site_disconnect > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs">{pendingCounts.site_disconnect}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* All Requests Tab */}
        <TabsContent value="requests" className="space-y-6">
          {viewMode === 'list' ? (
            <>
              <ChangeRequestList 
                workspaceId={workspaceId || ''}
                projectId={projectId}
                onViewRequest={(requestId) => {
                  // Handle view request
                }}
              />
              {isAdmin && (
                <ChangeRequestApprovalPanel 
                  workspaceId={workspaceId || ''}
                  projectId={projectId}
                />
              )}
            </>
          ) : (
            <div className="space-y-4">
              <Button variant="outline" onClick={handleBackToList}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to List
              </Button>
              {selectedRequestType === 'roll_in' && (
                <RollInRequestForm 
                  workspaceId={workspaceId || ''}
                  projectId={projectId}
                  onSuccess={handleBackToList}
                />
              )}
              {selectedRequestType === 'rollout' && (
                <RolloutRequestForm 
                  workspaceId={workspaceId || ''}
                  projectId={projectId}
                  onSuccess={handleBackToList}
                />
              )}
              {selectedRequestType === 'account_number_change' && (
                <AccountNumberChangeForm 
                  workspaceId={workspaceId || ''}
                  projectId={projectId}
                  onSuccess={handleBackToList}
                />
              )}
              {selectedRequestType === 'site_disconnection' && (
                <SiteDisconnectionForm 
                  workspaceId={workspaceId || ''}
                  projectId={projectId}
                  onSuccess={handleBackToList}
                />
              )}
            </div>
          )}
        </TabsContent>

        {/* Roll In Tab */}
        <TabsContent value="roll_in" className="space-y-6">
          {viewMode === 'list' ? (
            <>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">Roll In Requests</h2>
                <Button onClick={() => handleCreateRequest('roll_in')}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Roll In Request
                </Button>
              </div>
              <ChangeRequestList 
                workspaceId={workspaceId || ''}
                projectId={projectId}
                requestType="roll_in"
                onViewRequest={(requestId) => {
                  // Handle view request
                }}
              />
            </>
          ) : (
            <RollInRequestForm 
              workspaceId={workspaceId || ''}
              projectId={projectId}
              onSuccess={handleBackToList}
            />
          )}
        </TabsContent>

        {/* Rollout Tab */}
        <TabsContent value="rollout" className="space-y-6">
          {viewMode === 'list' ? (
            <>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">Rollout Requests</h2>
                <Button onClick={() => handleCreateRequest('rollout')}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Rollout Request
                </Button>
              </div>
              <ChangeRequestList 
                workspaceId={workspaceId || ''}
                projectId={projectId}
                requestType="rollout"
                onViewRequest={(requestId) => {
                  // Handle view request
                }}
              />
            </>
          ) : (
            <RolloutRequestForm 
              workspaceId={workspaceId || ''}
              projectId={projectId}
              onSuccess={handleBackToList}
            />
          )}
        </TabsContent>

        {/* Account Number Change Tab */}
        <TabsContent value="account_number_change" className="space-y-6">
          {viewMode === 'list' ? (
            <>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">Account Number Change Requests</h2>
                <Button onClick={() => handleCreateRequest('account_number_change')}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Account Change Request
                </Button>
              </div>
              <ChangeRequestList 
                workspaceId={workspaceId || ''}
                projectId={projectId}
                requestType="account_number_change"
                onViewRequest={(requestId) => {
                  // Handle view request
                }}
              />
            </>
          ) : (
            <AccountNumberChangeForm 
              workspaceId={workspaceId || ''}
              projectId={projectId}
              onSuccess={handleBackToList}
            />
          )}
        </TabsContent>

        {/* Site Disconnection Tab */}
        <TabsContent value="site_disconnection" className="space-y-6">
          {viewMode === 'list' ? (
            <>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">Site Disconnection Requests</h2>
                <Button onClick={() => handleCreateRequest('site_disconnection')}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Disconnection Request
                </Button>
              </div>
              <ChangeRequestList 
                workspaceId={workspaceId || ''}
                projectId={projectId}
                requestType="site_disconnection"
                onViewRequest={(requestId) => {
                  // Handle view request
                }}
              />
            </>
          ) : (
            <SiteDisconnectionForm 
              workspaceId={workspaceId || ''}
              projectId={projectId}
              onSuccess={handleBackToList}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

